<template>
  <div class="AlertCardContainer">
    <div v-if="awaitingAlertInfo" class="LoadingState">
      <div class="LoadingSpinner"></div>
      <span>Loading alerts...</span>
    </div>

    <div v-else class="ContentContainer" :class="{ 'is-loaded': !awaitingAlertInfo }">
      <div class="AboutSection" :style="{ opacity }">
        <h1 v-html="alertsTitle"></h1>
        <p v-if="alerts.length === 0">No active water quality alerts were returned by the Environmental Protection Agency. Please check back soon or try local sources for updates.</p>
        <p v-else>The following are active water quality alerts from the Environmental Protection Agency sorted by county. Reports are posted by local authorities and may still be displayed here after the period they refer to has passed. Check the provided PDF notices to read offical posted notices and exact wording of the alerts.</p>
      </div>
      <div class="GridContainer">
      
      <div class="alerts-grid">
        <template v-if="alerts.length === 0">
          <div :key="'no-alerts'" class="no-alerts">
            <span> No active alerts at the moment</span>
          </div>
        </template>
        
        <template v-else>
          <div v-for="alert in alerts" 
               :key="alert.beach_name" 
               class="alert-card">
            
            <div class="NoticeExpiryTopRight">
              {{ getExpiryMessage(alert) }}
            </div>

            <div class="alert-content">
              <div class="CountyName">{{ alert.county_name }}</div>
              <div class="BeachName">{{ alert.beach_name }}</div>
              <div class="BathingStatus">⚠️ &nbsp;{{ alert.bathing_restriction_type }}</div>
              <p class="BathingDescription">
                {{ alert.incident_description }}.
                <span>
                  <a :href="alert.bathing_notice_pdf" target="_blank" rel="noopener">Notice (PDF)</a>
                </span>
              </p>
            </div>

            <div class="alert-metadata">
              <span>Posted: {{ formatDate(alert.incident_start_date) }}</span>
              <span>Duration: {{ alert.incident_expected_duration }} Day(s)</span>
            </div>
          </div>
        </template>
      </div>

    </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InfoCard',
  data() {
    return {
      alerts: [],
      awaitingAlertInfo: true,
      opacity: 1
    }
  },
  computed: {
    alertsTitle() {
      const count = this.alerts.length;
      if (count === 0) return 'No Active Bathing Quality Alerts';
      const countSpan = `<span class="alert-count">${count}</span>`;
      return count === 1 
        ? `${countSpan} Active Bathing Quality Alert`
        : `${countSpan} Active Bathing Quality Alerts`;
    }
  },
  async created() {
    try {
      this.awaitingAlertInfo = true;
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? 'https://00h1g33v5j.execute-api.us-east-1.amazonaws.com/alerts/1'
        : '/api/api/v1/alerts';
        
      const response = await fetch(apiUrl);
      const data = await response.json();
      this.alerts = data.list.sort((a, b) => 
        a.county_name.localeCompare(b.county_name)
      );
     
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      this.awaitingAlertInfo = false;
    }
  },
  mounted() {
    const container = document.querySelector('.AlertCardContainer');
    container.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    const container = document.querySelector('.AlertCardContainer');
    container.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll(event) {
      const scrollPosition = event.target.scrollTop;
      this.opacity = Math.max(0, 1 - (scrollPosition / 200));
    },
    getExpiryMessage(alert) {
      const startDate = new Date(alert.incident_start_date);
      const expiryDate = new Date(startDate.setDate(startDate.getDate() + parseInt(alert.incident_expected_duration)));
      const today = new Date();
      
      // Reset time portion for accurate date comparison
      today.setHours(0, 0, 0, 0);
      expiryDate.setHours(0, 0, 0, 0);
      
      const diffTime = expiryDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) return 'Notice may have expired';
      if (diffDays === 0) return 'Notice expiring today';
      if (diffDays === 1) return 'Notice expires tomorrow';
      return `Expires in ${diffDays} days`;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = now - date;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const weeks = Math.floor(diffDays / 7);
      const months = Math.floor(diffDays / 30);
      const years = Math.floor(diffDays / 365);
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
      if (diffDays < 30) return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
      if (diffDays < 365) return `${months} ${months === 1 ? 'month' : 'months'} ago`;
      return `${years} ${years === 1 ? 'year' : 'years'} ago`;
    }
  }
}
</script>

<style lang="scss">
@keyframes riseUp {
  from {
    opacity: 0;
    transform: translateY(10px);
    visibility: hidden;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    visibility: visible;
  }
}

.AlertCardContainer {
  background-color: var(--dark-blue);
  position: absolute;
  top: 0;
  width: 100%;
  height: 100vh;
  overflow-y: scroll;
}

.LoadingState {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--off-white);
  
  .LoadingSpinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255,255,255,0.1);
    border-top-color: var(--yellow);
    border-radius: 50%;
    margin-bottom: 16px;
    animation: spin 1s linear infinite;
  }
  
  span {
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    opacity: 0.7;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.ContentContainer {
  opacity: 0;
  transform: translateY(20px);
  
  &.is-loaded {
    animation: riseUp 0.3s ease-out forwards;
  }
}

.AboutSection{
    position: sticky;  // Change from fixed to sticky
    top: 0;
    z-index: 1;
    font-size: 20px;
    padding: 8vw;
    padding-top: 20vw;
    color: var(--off-white);
    animation: rise .3s ease-in-out;
    
    h1{ letter-spacing: -1px; line-height: 1em;}
    p{ font-family: 'Poppins'; font-size: 12px;}
  }

.GridContainer {
  position: relative;  // Add positioning context
  z-index: 2;  // Higher than AboutSection
  display: flex;
  width: 100vw;
  place-items: center;
}

.alerts-grid {

  width: 100%;
  grid-template-rows: repeat(auto-fill, minmax(200px, 1fr));
  margin-top: 0;  // Remove top margin
  display: grid;
  gap: 6px;
  padding-bottom: 200px;
  place-items: center;

}

.alert-card {
  position: relative; // Ensure positioning context

  .NoticeExpiryTopRight {
    position: absolute;
    top: 10px;
    right: 14px;
    background: white;
    color: var(--dark-blue);
    font-size: 11px;
    border-radius: 4px;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.05);
    padding: 4px 8px;
    z-index: 2;
    min-width: 120px;
    text-align: right;
  }

  width: 90vw;
  font-family: 'Poppins', sans-serif;
  background: oklch(98.4% 0.003 247.858);
  border-radius: 6px;
  padding: 0;
  display: flex;
  flex-direction: column;

  h3, p, span{ color: var(--dark-blue) }
  h3 {
    font-size: 16px;
    margin: 0;
    padding-top: 6px;
    padding-bottom: 4px;
    font-weight: 500;
  }

  .BathingStatus{ 
    background-color: oklch(98.7% 0.022 95.277);
    border: 1px solid oklch(96.2% 0.059 95.617);
    color: var(--dark-blue);
    border-radius: 4px; 
    font-size: 11px;
    padding: 4px 8px;
    width: fit-content;
    margin-top: 8px;
    padding-bottom: 8px;
  }

  .BathingDescription{
    color: var(--dark-blue);
    margin-top: 16px;
    font-size: 12px;
  }

  .BeachName{
    font-family: "recoletablack";
    color: var(--dark-blue);
    font-size: 22px;
    padding-top: 0px;
    padding-bottom: 2px;
  }

  .CountyName {
    color: var(--dark-blue);
    font-size: 11px;
    opacity: 0.7;
    margin-top: 20px;
  }

  .alert-icon {
    font-size: 20px;
    padding-top: 2px;
  }

  .alert-content {
    flex: 1;
    padding: 15px;

    a { font-size: 10px;}
  }


  .alert-metadata {
    font-size: 12px;
    color: var(--dark-blue);
    background: rgba(0, 0, 0, 0.03);
    padding: 8px 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    

    span {
      font-size: 10px;
      color: var(--dark-blue);
      opacity: 0.7;
      padding-right: 12px;
    }
  }
}

.no-alerts {
 
  padding: 20px;
  text-align: center;
  grid-column: 1 / -1;
  
  span {
    color: var(--dark-blue);
    font-size: 18px;
    font-weight: 500;
    display: block;
    margin-bottom: 8px;
  }
  
  p {
    color: var(--dark-blue);
    opacity: 0.7;
    font-size: 14px;
    margin: 0;
  }
}

.alert-count {
  color: var(--yellow);
}
</style>

